import { useEffect, useRef, useState } from 'react';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { useConfig } from 'hooks/use-config';
import { useToast } from 'hooks/use-toast';
import { HttpError } from 'lib/https';
import { useGlobalMutation, useGlobalQuery } from 'state/query-client/hooks';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { utilitiesErrorMessage } from 'features/subscription/utils/seat-pools';
import {
  CREDITS_QUERY_KEY,
  FINAL_TOP_UP_STATES,
  HISTORY_PAGE_SIZE,
  PLAN_REQUEST_QUERY_KEY,
  TOP_UP_POLL_INTERVAL_MS,
  TOP_UP_POLL_TIMEOUT_MS,
} from '../constants/credits';
import {
  cancelTopUp,
  createTopUp,
  downloadInvoicePdf,
  findTopUpByPayment,
  getCreditAccess,
  getCreditSpend,
  getCreditSummary,
  getInvoices,
  getLedger,
  getPlanRequestStatus,
  getSavedCards,
  getTopUp,
  getTopUps,
  previewCoupon,
  requestPlanChange,
  setCreditAccess,
} from '../services/credits-service';
import type {
  CouponPreviewRequest,
  CouponQuote,
  CreateTopUpResponse,
  CreditAccess,
  Invoice,
  TopUp,
} from '../types';

/** Every credits query is kept per organization, so switching organization never shows another's. */
const useCreditsScope = () => {
  const selectedOrgId = useAuthStore((s) => s.selectedOrgId);
  const user = useCommonStore((s) => s.user);
  return [CREDITS_QUERY_KEY, selectedOrgId || user?.organizationId || ''];
};

export const isFinalTopUp = (topUp?: TopUp | null) =>
  !!topUp && FINAL_TOP_UP_STATES.includes(topUp.state);

/** `refetchOnMount` is turned off by the many places that only need to know whether credits exist. */
export const useCreditSummary = (refetchOnMount: 'always' | false = 'always') => {
  const { configurationSettings } = useConfig();
  const scope = useCreditsScope();
  // Without a session the server answers 401, which starts a token refresh on the login page.
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  return useGlobalQuery({
    queryKey: [...scope, 'summary'],
    queryFn: () => getCreditSummary(configurationSettings),
    enabled: isAuthenticated,
    refetchOnMount,
  });
};

/** True while top-up credits cover this member, so a full plan limit does not stop them. */
export const useTopUpCoversUsage = () => {
  const { data } = useCreditSummary();
  return !!data?.enabled && data.hasAccess !== false && data.balanceCredits > 0;
};

interface CreateTopUpInput {
  amountChf: number;
  paymentMethodId?: string;
  couponCode?: string;
}

/**
 * Starts a top-up. One click keeps one `requestId`: a retry after the network dropped sends the
 * same id, so the server returns the same top-up. Once the server has answered, the next click is
 * a new top-up and gets a new id.
 */
export const useCreateTopUp = () => {
  const { configurationSettings } = useConfig();
  const queryClient = useQueryClient();
  const scope = useCreditsScope();
  const request = useRef<{ key: string; id: string } | null>(null);

  // Back from the checkout page, the browser restores this page as it was, before the top-up
  // existed. Nothing remounts and focus refetch is off, so read the credits again to show it.
  useEffect(() => {
    const refetchOnRestore = (event: PageTransitionEvent) => {
      if (event.persisted) queryClient.invalidateQueries({ queryKey: [CREDITS_QUERY_KEY] });
    };
    window.addEventListener('pageshow', refetchOnRestore);
    return () => window.removeEventListener('pageshow', refetchOnRestore);
  }, [queryClient]);

  return useGlobalMutation<CreateTopUpResponse, unknown, CreateTopUpInput>({
    mutationFn: (input) => {
      const key = `${input.amountChf}|${input.paymentMethodId ?? ''}|${input.couponCode ?? ''}`;
      if (request.current?.key !== key) request.current = { key, id: uuidv4() };
      return createTopUp({ ...input, requestId: request.current.id }, configurationSettings);
    },
    onSettled: (_data, error) => {
      if (!error || error instanceof HttpError) request.current = null;
      queryClient.invalidateQueries({ queryKey: [...scope, 'topups'] });
    },
  });
};

export const usePreviewCoupon = () => {
  const { configurationSettings } = useConfig();

  return useGlobalMutation<CouponQuote, unknown, CouponPreviewRequest>({
    mutationFn: (input) => previewCoupon(input, configurationSettings),
  });
};

/** Whether the member already has a request waiting. Read when the request dialog opens. */
export const usePlanRequestStatus = (enabled: boolean) => {
  const { configurationSettings } = useConfig();

  return useGlobalQuery({
    queryKey: [PLAN_REQUEST_QUERY_KEY],
    queryFn: () => getPlanRequestStatus(configurationSettings),
    enabled: enabled && !!configurationSettings,
    staleTime: 0,
    retry: false,
  });
};

/** `limitName` is the limit the member reached; it goes into the email the admins get. */
export const useRequestPlanChange = () => {
  const { configurationSettings } = useConfig();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (limitName: string) => requestPlanChange(limitName, configurationSettings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PLAN_REQUEST_QUERY_KEY] });
      toast({
        variant: 'success',
        title: t('PLAN_REQUEST_SENT', 'Request sent'),
        description: t(
          'PLAN_REQUEST_SENT_DESCRIPTION',
          'Your admin has been asked to move you to another plan.'
        ),
      });
    },
    onError: () =>
      toast({
        variant: 'destructive',
        title: t('PLAN_REQUEST_FAILED', 'The request could not be sent'),
        description: t('TRY_AGAIN_IN_A_MOMENT', 'Try again in a moment.'),
      }),
  });
};

export const useCancelTopUp = () => {
  const { configurationSettings } = useConfig();
  const queryClient = useQueryClient();
  const scope = useCreditsScope();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (topUpId: string) => cancelTopUp(topUpId, configurationSettings),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: scope }),
    onError: () =>
      toast({
        variant: 'destructive',
        title: t('TOPUP_CANCEL_FAILED', 'The top-up could not be cancelled'),
        description: t('TRY_AGAIN_IN_A_MOMENT', 'Try again in a moment.'),
      }),
  });
};

/**
 * Asks the server for a top-up every 2 seconds until it is Paid, Failed, Cancelled or Expired.
 * After 60 seconds it stops and reports `timedOut`, so the screen can say it is still confirming.
 */
export const useTopUpStatus = (topUpId?: string, initialTopUp?: TopUp) => {
  const { configurationSettings } = useConfig();
  const queryClient = useQueryClient();
  const scope = useCreditsScope();
  const [isTimedOut, setIsTimedOut] = useState(false);

  useEffect(() => {
    setIsTimedOut(false);
    if (!topUpId) return undefined;
    const timer = setTimeout(() => setIsTimedOut(true), TOP_UP_POLL_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [topUpId]);

  const query = useGlobalQuery({
    queryKey: [...scope, 'topup', topUpId],
    queryFn: () => getTopUp(topUpId as string, configurationSettings),
    enabled: !!topUpId,
    initialData: initialTopUp,
    refetchInterval: (current) =>
      isFinalTopUp(current.state.data) || isTimedOut ? false : TOP_UP_POLL_INTERVAL_MS,
  });

  const isPaid = query.data?.state === 'Paid';
  useEffect(() => {
    if (isPaid) queryClient.invalidateQueries({ queryKey: scope });
    // The scope is a new array on every render, so only the payment's own outcome re-runs this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaid, queryClient]);

  return { topUp: query.data, isTimedOut: isTimedOut && !isFinalTopUp(query.data) };
};

/** The top-up behind a payment, or null when it is not one. */
export const useTopUpByPayment = (paymentDetailId: string) => {
  const { configurationSettings } = useConfig();
  const scope = useCreditsScope();

  return useGlobalQuery({
    queryKey: [...scope, 'payment', paymentDetailId],
    queryFn: () => findTopUpByPayment(paymentDetailId, configurationSettings),
    retry: 2,
  });
};

export const useTopUps = (page: number) => {
  const { configurationSettings } = useConfig();
  const scope = useCreditsScope();

  return useGlobalQuery({
    queryKey: [...scope, 'topups', page],
    queryFn: () => getTopUps(page, HISTORY_PAGE_SIZE, configurationSettings),
    placeholderData: keepPreviousData,
    refetchOnMount: 'always',
  });
};

export const useLedger = (page: number) => {
  const { configurationSettings } = useConfig();
  const scope = useCreditsScope();

  return useGlobalQuery({
    queryKey: [...scope, 'ledger', page],
    queryFn: () => getLedger(page, HISTORY_PAGE_SIZE, configurationSettings),
    placeholderData: keepPreviousData,
    refetchOnMount: 'always',
  });
};

/** The organization's top-up spend, or one person's when `userId` is given. */
export const useCreditSpend = (isEnabled: boolean, userId?: string) => {
  const { configurationSettings } = useConfig();
  const scope = useCreditsScope();

  return useGlobalQuery({
    queryKey: [...scope, 'spend', userId ?? ''],
    queryFn: () => getCreditSpend(configurationSettings, userId),
    enabled: isEnabled,
    refetchOnMount: 'always',
  });
};

/** Which of these members an admin switched off from the top-up credits; everyone else is on. */
export const useCreditAccess = (userIds: string[], isEnabled: boolean) => {
  const { configurationSettings } = useConfig();
  const scope = useCreditsScope();

  return useGlobalQuery({
    queryKey: [...scope, 'access', userIds],
    queryFn: () => getCreditAccess(userIds, configurationSettings),
    enabled: isEnabled && userIds.length > 0,
    placeholderData: keepPreviousData,
    // useGlobalQuery turns refetch-on-mount off; a switch made on another tab must show here.
    refetchOnMount: 'always',
  });
};

/** Switches one member on or off. The screen follows at once, and goes back if the server refuses. */
export const useSetCreditAccess = () => {
  const { configurationSettings } = useConfig();
  const queryClient = useQueryClient();
  const scope = useCreditsScope();
  const { toast } = useToast();
  const { t } = useTranslation();
  const accessKey = [...scope, 'access'];

  return useGlobalMutation({
    mutationFn: ({ userId, allowed }: { userId: string; allowed: boolean }) =>
      setCreditAccess(userId, allowed, configurationSettings),
    onMutate: ({ userId, allowed }) => {
      queryClient.setQueriesData<CreditAccess>({ queryKey: accessKey }, (current) => {
        if (!current) return current;
        const others = current.blockedUserIds.filter((id) => id !== userId);
        return { ...current, blockedUserIds: allowed ? others : [...others, userId] };
      });
    },
    onError: () =>
      toast({
        variant: 'destructive',
        title: t('CREDIT_ACCESS_FAILED', 'Credit access could not be changed'),
        description: t('TRY_AGAIN_IN_A_MOMENT', 'Try again in a moment.'),
      }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: accessKey }),
  });
};

export const useInvoices = () => {
  const { configurationSettings } = useConfig();
  const scope = useCreditsScope();

  return useGlobalQuery({
    queryKey: [...scope, 'invoices'],
    queryFn: () => getInvoices(configurationSettings),
    refetchOnMount: 'always',
  });
};

export const useDownloadInvoice = () => {
  const { configurationSettings } = useConfig();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (invoice: Invoice) => downloadInvoicePdf(invoice, configurationSettings),
    onError: () =>
      toast({
        variant: 'destructive',
        title: t('INVOICE_DOWNLOAD_FAILED', 'The invoice could not be downloaded'),
        description: t('TRY_AGAIN_IN_A_MOMENT', 'Try again in a moment.'),
      }),
  });
};

export const useSavedCards = () => {
  const { configurationSettings } = useConfig();
  const scope = useCreditsScope();

  return useGlobalQuery({
    queryKey: [...scope, 'cards'],
    queryFn: () => getSavedCards(configurationSettings),
    refetchOnMount: 'always',
  });
};
